/**
 * Schema Node Card Component
 *
 * Custom React Flow node for displaying Knowledge Graph entities.
 * Supports two modes:
 * 1. KG-First Mode: Shows conceptual entities with KG Area badges
 * 2. Legacy Mode: Shows tables converted to nodes
 *
 * Displays:
 * - Entity name (concept name)
 * - KG Area badge (for KG-First mode)
 * - Properties list with types
 * - Entity type indicators (Master Data, Transactional, Analytical)
 */

import React, { memo } from 'react';
import { Handle, Position, NodeProps } from '@xyflow/react';
import {
  CircleDot,
  Key,
  Layers,
  Star,
  ChevronDown,
  ChevronUp,
  Database,
  Activity,
  BarChart3,
  Building2,
} from 'lucide-react';
import { useState } from 'react';

interface NodeProperty {
  name: string;
  type?: string;
  isPrimaryKey?: boolean;
  isForeignKey?: boolean;
  required?: boolean;
  unique?: boolean;
  indexed?: boolean;
  description?: string;
}

interface SchemaNodeData {
  label: string;
  sourceTableName?: string; // Original source system table name (e.g., "MARA" when label is "Material")
  category?: string; // KG Area name (e.g., "Product Hub", "Order-to-Cash")
  description?: string;
  properties: NodeProperty[];
  isCentral?: boolean;
  connectionCount?: number;
  colors: {
    bg: string;
    border: string;
    text: string;
  };
  // KG-First specific fields
  kgAreaId?: string;
  kgAreaName?: string;
  isMasterData?: boolean;
  isTransactional?: boolean;
  isAnalytical?: boolean;
}

const MAX_VISIBLE_PROPERTIES = 5;

// Get icon for entity type
const getEntityTypeIcon = (data: SchemaNodeData) => {
  if (data.isMasterData || data.category?.includes('Hub')) {
    return <Database className="w-4 h-4 text-white" />;
  }
  if (data.isTransactional || data.category?.includes('-to-')) {
    return <Activity className="w-4 h-4 text-white" />;
  }
  if (data.isAnalytical || data.category?.includes('Analytics') || data.category?.includes('Metrics')) {
    return <BarChart3 className="w-4 h-4 text-white" />;
  }
  return <CircleDot className="w-4 h-4 text-white" />;
};

// Get entity type label
const getEntityTypeLabel = (data: SchemaNodeData): string | null => {
  if (data.isMasterData || data.category?.includes('Hub')) {
    return 'Master Data';
  }
  if (data.isTransactional || data.category?.includes('-to-')) {
    return 'Transactional';
  }
  if (data.isAnalytical || data.category?.includes('Analytics') || data.category?.includes('Metrics')) {
    return 'Analytical';
  }
  return null;
};

export const SchemaNodeCard: React.FC<NodeProps> = memo(({ data, selected }) => {
  const nodeData = data as unknown as SchemaNodeData;
  const [expanded, setExpanded] = useState(false);

  const visibleProperties = expanded
    ? nodeData.properties
    : nodeData.properties.slice(0, MAX_VISIBLE_PROPERTIES);
  const hasMoreProperties = nodeData.properties.length > MAX_VISIBLE_PROPERTIES;

  const entityTypeLabel = getEntityTypeLabel(nodeData);

  return (
    <div
      className={`
        relative min-w-[260px] max-w-[320px] rounded-lg shadow-lg
        transition-all duration-200 ease-in-out
        ${selected ? 'ring-2 ring-offset-2 ring-blue-500 scale-105' : ''}
      `}
      style={{
        backgroundColor: nodeData.colors.bg,
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: nodeData.colors.border,
      }}
    >
      {/* Input handle (left) */}
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3 !h-3 !border-2 !border-white"
        style={{ backgroundColor: nodeData.colors.border }}
      />

      {/* Output handle (right) */}
      <Handle
        type="source"
        position={Position.Right}
        className="!w-3 !h-3 !border-2 !border-white"
        style={{ backgroundColor: nodeData.colors.border }}
      />

      {/* Header - Entity Name */}
      <div
        className="px-4 py-2 rounded-t-lg flex items-center justify-between"
        style={{ backgroundColor: nodeData.colors.border }}
      >
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            {getEntityTypeIcon(nodeData)}
            <span className="font-bold text-white text-sm">{nodeData.label}</span>
          </div>
        </div>
        {nodeData.isCentral && (
          <span title="Central entity">
            <Star className="w-4 h-4 text-yellow-300 fill-yellow-300" />
          </span>
        )}
      </div>

      {/* KG Area Badge */}
      {nodeData.category && nodeData.category !== 'default' && (
        <div className="px-4 py-1.5 border-b flex items-center justify-between" style={{ borderColor: `${nodeData.colors.border}40` }}>
          <span
            className="text-xs font-medium px-2 py-0.5 rounded-full flex items-center gap-1"
            style={{
              backgroundColor: `${nodeData.colors.border}20`,
              color: nodeData.colors.text,
            }}
          >
            <Layers className="w-3 h-3" />
            {nodeData.category}
          </span>
          {entityTypeLabel && (
            <span
              className="text-[10px] font-medium px-1.5 py-0.5 rounded"
              style={{
                backgroundColor: `${nodeData.colors.border}15`,
                color: nodeData.colors.text,
              }}
            >
              {entityTypeLabel}
            </span>
          )}
        </div>
      )}

      {/* Properties list */}
      <div className="px-3 py-2">
        <div className="text-[10px] uppercase tracking-wider text-neutral-400 mb-1 font-medium">
          Properties
        </div>
        {nodeData.properties.length === 0 ? (
          <p className="text-xs text-neutral-400 italic">No properties defined</p>
        ) : (
          <>
            <ul className="space-y-1">
              {visibleProperties.map((prop, index) => (
                <li
                  key={`${prop.name}-${index}`}
                  className="flex items-center justify-between text-xs"
                >
                  <div className="flex items-center gap-1.5">
                    {prop.isPrimaryKey ? (
                      <span title="Identifier">
                        <Key className="w-3 h-3 text-amber-500" />
                      </span>
                    ) : prop.indexed ? (
                      <span title="Indexed" className="w-3 h-3 text-blue-400 text-[10px] font-bold">
                        idx
                      </span>
                    ) : (
                      <span className="w-3 h-3 text-center text-neutral-300">•</span>
                    )}
                    <span
                      className={`font-medium ${
                        prop.isPrimaryKey
                          ? 'text-neutral-800'
                          : 'text-neutral-600'
                      }`}
                    >
                      {prop.name}
                    </span>
                    {prop.required && (
                      <span className="text-red-500" title="Required">
                        *
                      </span>
                    )}
                  </div>
                  <span className="text-neutral-400 font-mono text-[10px]">
                    {prop.type || 'String'}
                  </span>
                </li>
              ))}
            </ul>

            {/* Show more/less button */}
            {hasMoreProperties && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpanded(!expanded);
                }}
                className="mt-2 w-full flex items-center justify-center gap-1 text-xs text-neutral-500 hover:text-neutral-700 transition-colors"
              >
                {expanded ? (
                  <>
                    <ChevronUp className="w-3 h-3" />
                    Show less
                  </>
                ) : (
                  <>
                    <ChevronDown className="w-3 h-3" />
                    +{nodeData.properties.length - MAX_VISIBLE_PROPERTIES} more
                  </>
                )}
              </button>
            )}
          </>
        )}
      </div>

      {/* Footer with relationship count */}
      {nodeData.connectionCount !== undefined && nodeData.connectionCount > 0 && (
        <div
          className="px-3 py-1.5 text-xs text-center rounded-b-lg"
          style={{
            backgroundColor: `${nodeData.colors.border}10`,
            color: nodeData.colors.text,
          }}
        >
          {nodeData.connectionCount} relationship{nodeData.connectionCount !== 1 ? 's' : ''}
        </div>
      )}
    </div>
  );
});

SchemaNodeCard.displayName = 'SchemaNodeCard';

export default SchemaNodeCard;
